/**
 * Header Context - Allows screens to customize the header
 */

import { createContext, useContext, useState, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface HeaderConfig {
  title?: string;
  icon?: LucideIcon;
  showBackButton?: boolean;
  onBackClick?: () => void;
  showLogo?: boolean;
  showUserMenu?: boolean; // Show user avatar dropdown menu
  rightContent?: ReactNode; // Custom JSX content for the right side
}

interface HeaderContextType {
  headerConfig: HeaderConfig;
  setHeaderConfig: (config: HeaderConfig) => void;
  resetHeader: () => void;
  registerComponent: () => void;
  unregisterComponent: () => void;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

const DEFAULT_HEADER_CONFIG: HeaderConfig = {
  title: "WhenCanIQuit",
  showBackButton: false,
  showLogo: true, // Hide logo by default, only show when explicitly requested
  rightContent: null, // Will be replaced with default controls in Header component
};

export function HeaderProvider({ children }: { children: ReactNode }) {
  const [headerConfigData, setHeaderConfigData] = useState<HeaderConfig>(DEFAULT_HEADER_CONFIG);
  const activeComponentsRef = useRef(0);

  const resetHeader = useCallback(() => {
    // Only reset if no components are actively using the header
    if (activeComponentsRef.current <= 0) {
      setHeaderConfigData(DEFAULT_HEADER_CONFIG);
    }
  }, []);

  const setHeaderConfig = useCallback((config: HeaderConfig) => {
    setHeaderConfigData(config);
  }, []);

  const registerComponent = useCallback(() => {
    activeComponentsRef.current += 1;
  }, []);

  const unregisterComponent = useCallback(() => {
    activeComponentsRef.current = Math.max(0, activeComponentsRef.current - 1);
  }, []);

  return (
    <HeaderContext.Provider value={{ 
      headerConfig: headerConfigData, 
      setHeaderConfig, 
      resetHeader,
      registerComponent,
      unregisterComponent
    }}>
      {children}
    </HeaderContext.Provider>
  );
}

export function useHeader() {
  const context = useContext(HeaderContext);
  if (context === undefined) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
}
